import { z } from 'zod';
import { conversationIdSchema } from './chat';
import {
  audioAttachmentMimeSchema,
  chatAttachmentKindSchema,
} from './audio-transcription';
import { supportedRagMimeSchema } from './rag-mime-types';

export const chatAttachmentExtractionStatusSchema = z.enum([
  'pending',
  'processing',
  'ready',
  'failed',
]);

export const chatAttachmentMediaTypeSchema = z.union([
  supportedRagMimeSchema,
  audioAttachmentMimeSchema,
]);

export const chatAttachmentRefSchema = z.object({
  attachmentId: z.uuid({
    error: 'L identifiant de piece jointe est invalide.',
  }),
  durationMs: z.number().int().positive().nullable().optional(),
  filename: z.string().trim().min(1, {
    error: 'Le nom du fichier joint est requis.',
  }),
  kind: chatAttachmentKindSchema.default('document'),
  mediaType: chatAttachmentMediaTypeSchema,
  sizeBytes: z.number().int().positive({
    error: 'La taille du fichier joint est invalide.',
  }),
}).superRefine((attachment, context) => {
  if (attachment.kind === 'audio' && !audioAttachmentMimeSchema.safeParse(attachment.mediaType).success) {
    context.addIssue({
      code: 'custom',
      message: 'Le type MIME audio de la pièce jointe est invalide.',
      path: ['mediaType'],
    });
  }

  if (attachment.kind === 'document' && !supportedRagMimeSchema.safeParse(attachment.mediaType).success) {
    context.addIssue({
      code: 'custom',
      message: 'Le type MIME documentaire de la pièce jointe est invalide.',
      path: ['mediaType'],
    });
  }
});

export const uploadChatAttachmentResponseSchema = z.object({
  attachmentId: z.uuid(),
  charCount: z.number().int().nonnegative().nullable(),
  extractionStatus: chatAttachmentExtractionStatusSchema,
  filename: z.string().trim().min(1),
  mediaType: supportedRagMimeSchema,
  preview: z.string().nullable(),
  sizeBytes: z.number().int().positive(),
  truncated: z.boolean(),
});

export const getChatAttachmentResponseSchema = uploadChatAttachmentResponseSchema.extend({
  downloadUrl: z.string().trim().min(1).nullable(),
  errorCode: z.string().trim().min(1).nullable(),
});

export const uploadChatAttachmentQuerySchema = z.object({
  conversationId: conversationIdSchema.optional(),
});

export type ChatAttachmentExtractionStatusDTO = z.infer<typeof chatAttachmentExtractionStatusSchema>;
export type ChatAttachmentKindDTO = z.infer<typeof chatAttachmentKindSchema>;
export type ChatAttachmentRefDTO = z.infer<typeof chatAttachmentRefSchema>;
export type UploadChatAttachmentResponseDTO = z.infer<typeof uploadChatAttachmentResponseSchema>;
export type GetChatAttachmentResponseDTO = z.infer<typeof getChatAttachmentResponseSchema>;
